import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Taxonomy Term Search Controller
 * ================================
 * A single-select taxonomy term picker with search.
 * Searches taxonomy terms via a remote JSON endpoint and allows selecting one.
 *
 * Required Structure:
 * ------------------
 * ```html
 * <div data-controller="taxonomy-term-search"
 *      data-taxonomy-term-search-url-value="/search/path">
 *   <input data-taxonomy-term-search-target="input"
 *          data-action="input->taxonomy-term-search#search">
 *   <div data-taxonomy-term-search-target="results"></div>
 *   <input type="hidden" data-taxonomy-term-search-target="selectedId">
 * </div>
 * ```
 */
export default class extends Controller {
  static targets = ["input", "results", "selectedId"]
  static values = {
    url: String
  }

  declare readonly inputTarget: HTMLInputElement
  declare readonly resultsTarget: HTMLElement
  declare readonly selectedIdTarget: HTMLInputElement
  declare urlValue: string

  private searchTimeout: ReturnType<typeof setTimeout> | null = null

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query: string) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) return

      const terms = await response.json()
      this.renderResults(terms)
    } catch {
      // Silently handle network errors
    }
  }

  renderResults(terms: Array<{ id: number; name: string; code: string; path: string }>) {
    if (terms.length === 0) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-base-content/60">${i18n.t("taxonomy_term_search.no_results")}</div>`
      return
    }

    this.resultsTarget.innerHTML = terms.map(term =>
      `<button type="button"
               class="w-full text-left px-3 py-2 hover:bg-base-200 text-sm cursor-pointer border-b border-base-200 last:border-0"
               data-action="click->taxonomy-term-search#selectTerm"
               data-term-id="${term.id}"
               data-term-name="${this.escapeHtml(term.name)}"
               data-term-code="${this.escapeHtml(term.code)}">
        <div class="font-medium">${this.escapeHtml(term.name)}</div>
        <div class="text-xs text-base-content/60">${this.escapeHtml(term.path)}</div>
      </button>`
    ).join("")
  }

  selectTerm(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const termId = button.dataset.termId
    const termName = button.dataset.termName
    const termCode = button.dataset.termCode

    if (!termId || !termName) return

    this.selectedIdTarget.value = termId
    this.inputTarget.value = `${termName} (${termCode})`
    this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-success">${i18n.t("taxonomy_term_search.selected", { name: termName })}</div>`
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
